import { CloseCircleOutlined } from '@ant-design/icons';
import { Button, Input } from 'antd';
import { useState } from 'react';
import { Optional } from '../../Shared';

export type TypeAheadOption = {
  /**
   * The label to show on the drop down option (while searching)
   */
  readonly label: string;
  /**
   * The value to associate with the drop down option (shown when hover)
   */
  readonly value: string;
  /**
   * The ID of the selected item
   */
  readonly id: string;
};

type TypeAheadFuncProps = {
  readonly onSelect: (value: string) => void;
  readonly onSearch: (value: string) => void;
  readonly onClear?: () => void;
};

type TypeAheadProps = TypeAheadFuncProps & {
  readonly options: readonly TypeAheadOption[];
  readonly value?: string;
  readonly id?: string;
  readonly className?: string;
  readonly ariaLabel?: string;
  readonly placeholder?: string;
};

/**
 * A component that displays possible search results in dropdown list and perform actual search when options are selected.
 */
const TypeAhead = ({
  options,
  value,
  id,
  className,
  ariaLabel,
  placeholder,
  onSelect,
  onSearch,
  onClear,
}: TypeAheadProps) => {
  const [input, setInput] = useState<string>(value ?? '');

  const [optionsHidden, setOptionsHidden] = useState<boolean>(true);

  const hasResults = options.length > 0;

  const showClearButton = input !== '';

  return (
    <div id={id} className={className}>
      <Input
        className={`${className ?? 'typeahead'}-input`}
        type="search"
        onChange={(e) => {
          setInput(e.currentTarget.value);
          onSearch(e.currentTarget.value);
        }}
        value={input}
        style={{
          width: '100%',
        }}
        onSelect={() => setOptionsHidden(false)}
        aria-label={ariaLabel}
        placeholder={placeholder}
      ></Input>

      <Optional show={showClearButton}>
        <Button
          type="text"
          className="typeahead-clear-input-button"
          aria-label="Clear search text"
          style={{
            width: '20px',
            height: '20px',
            padding: 0,
            marginRight: '10px',
            position: 'absolute',
            right: 0,
            top: '4px',
            opacity: 0.5,
          }}
          onClick={() => {
            setOptionsHidden(true);
            setInput('');
            onSearch('');
            if (onClear !== undefined) {
              onClear();
            }
          }}
        >
          <CloseCircleOutlined />
        </Button>
      </Optional>

      <div
        style={{
          maxHeight: '300px',
          overflow: 'auto',
          border: '1px solid lightgrey',
          position: 'absolute',
          zIndex: 1,
          background: 'white',
          width: '95%',
        }}
        className={`${className ?? 'typeahead'}-options`}
        hidden={optionsHidden || !hasResults}
      >
        <Optional show={hasResults}>
          {options.map((opt, i) => (
            <option
              key={i}
              value={opt.id}
              title={opt.value}
              className="typeahead-option-button"
              style={{
                padding: 10,
              }}
              onClick={() => {
                onSelect(opt.id);
                setInput(opt.label);
                setOptionsHidden(true);
              }}
            >
              {opt.label}
            </option>
          ))}
        </Optional>
      </div>
    </div>
  );
};

export default TypeAhead;
